import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSmsTestingService } from './sms-testing.composable'
import type { SmsComposeBody, SmsMessage } from './sms-testing.types'
import { getErrorMessage } from '@/utils/error-handler'

interface SmsFilters {
  to: string
  from: string
  /** Scope to a single merchant. Driven by the `?merchantId=X` URL query. */
  merchantId: number | null
  /**
   * ISO timestamp lower bound — only return messages received at or
   * after this. Driven by the time-range chip row in SmsList. Null
   * means "all time" / no lower bound.
   */
  since: string | null
  /**
   * Free-text search — server-side OR'd substring across body, from,
   * and to. Driven by the search input (debounced).
   */
  q: string
}

export const useSmsTestingStore = defineStore('adx-sms-testing', () => {
  const service = useSmsTestingService()

  const messages = ref<SmsMessage[]>([])
  const listLoading = ref(false)
  // Cursor returned by the last list call — null means we've reached the
  // end of the result set (or haven't fetched yet). Drives the Load more
  // button's enabled state in SmsList.
  const nextCursor = ref<string | null>(null)
  const loadMoreLoading = ref(false)
  const filters = ref<SmsFilters>({ to: '', from: '', merchantId: null, since: null, q: '' })

  const detail = ref<SmsMessage | null>(null)
  const detailLoading = ref(false)

  const composeLoading = ref(false)
  const clearLoading = ref(false)

  const error = ref<string | null>(null)
  const hasError = computed(() => error.value !== null)
  const hasMore = computed(() => nextCursor.value !== null)

  function clearError(): void {
    error.value = null
  }

  // Default page size. Callers that scan the full retained window (e.g. the
  // Template Coverage drain) pass a larger `limit`; the infinite-scroll list
  // view uses this default so scroll ticks stay small and snappy.
  const DEFAULT_PAGE_LIMIT = 200

  function buildParams(cursor?: string, limit: number = DEFAULT_PAGE_LIMIT): Record<string, unknown> {
    return {
      ...(filters.value.to ? { to: filters.value.to } : {}),
      ...(filters.value.from ? { from: filters.value.from } : {}),
      ...(filters.value.merchantId !== null ? { merchantId: filters.value.merchantId } : {}),
      ...(filters.value.since ? { since: filters.value.since } : {}),
      ...(filters.value.q ? { q: filters.value.q } : {}),
      ...(cursor ? { cursor } : {}),
      limit
    }
  }

  async function fetchList(limit: number = DEFAULT_PAGE_LIMIT): Promise<void> {
    listLoading.value = true
    error.value = null
    try {
      const page = await service.listPage(buildParams(undefined, limit))
      messages.value = page.messages
      nextCursor.value = page.nextCursor
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load SMS list')
    } finally {
      listLoading.value = false
    }
  }

  /**
   * Append the next page to the existing list. No-op when `nextCursor`
   * is null (terminal page) or another load is already in flight.
   */
  async function loadMore(limit: number = DEFAULT_PAGE_LIMIT): Promise<void> {
    if (!nextCursor.value || loadMoreLoading.value) return
    loadMoreLoading.value = true
    error.value = null
    try {
      const page = await service.listPage(buildParams(nextCursor.value, limit))
      messages.value = [...messages.value, ...page.messages]
      nextCursor.value = page.nextCursor
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load more SMS')
    } finally {
      loadMoreLoading.value = false
    }
  }

  /**
   * Live-tail refresh — re-fetches the first page and prepends only the
   * messages not already loaded, WITHOUT discarding pages the user pulled in
   * via `loadMore()` or snapping their scroll position back to the top. Used
   * by the 5s live poll so the inbox tails new captures while infinite scroll
   * holds the loaded history.
   *
   * Why not `fetchList()`: that replaces the whole list with page 1, which
   * with infinite scroll meant every poll tick wiped everything scrolled past
   * the first 100. `refreshHead()` is the merge-at-head variant.
   *
   * Dedupes by id. `nextCursor` is left untouched — it points at the loaded
   * tail so `loadMore()` keeps paging from where the user scrolled to. If
   * more than one page (`limit`) of new messages arrived since the last
   * refresh — implausible at a 5s cadence — only the newest page is merged;
   * a manual refresh / filter change (`fetchList`) reconciles the rest.
   * Failures are swallowed: a dropped poll tick just retries next interval.
   */
  async function refreshHead(): Promise<void> {
    try {
      const page = await service.listPage(buildParams())
      if (messages.value.length === 0) {
        // Nothing loaded yet (poll raced ahead of the initial fetch) — adopt
        // the page wholesale so `nextCursor` is correct for infinite scroll.
        messages.value = page.messages
        nextCursor.value = page.nextCursor
        return
      }
      const known = new Set(messages.value.map((m) => m.id))
      const fresh = page.messages.filter((m) => !known.has(m.id))
      if (fresh.length > 0) messages.value = [...fresh, ...messages.value]
    } catch {
      /* non-fatal — keep the current list and retry on the next tick */
    }
  }

  async function fetchDetail(id: string): Promise<void> {
    detailLoading.value = true
    error.value = null
    try {
      detail.value = await service.get(id)
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load SMS')
    } finally {
      detailLoading.value = false
    }
  }

  async function clearInbox(): Promise<number> {
    if (!filters.value.to) return 0
    clearLoading.value = true
    error.value = null
    try {
      const deleted = await service.clear(filters.value.to)
      messages.value = []
      return deleted
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to clear SMS inbox')
      throw err
    } finally {
      clearLoading.value = false
    }
  }

  async function composeMessage(body: SmsComposeBody): Promise<SmsMessage> {
    composeLoading.value = true
    error.value = null
    try {
      const created = await service.compose(body)
      messages.value = [created, ...messages.value]
      return created
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to send SMS')
      throw err
    } finally {
      composeLoading.value = false
    }
  }

  function reset(): void {
    messages.value = []
    detail.value = null
    nextCursor.value = null
    error.value = null
  }

  return {
    messages,
    listLoading,
    nextCursor,
    loadMoreLoading,
    hasMore,
    filters,
    detail,
    detailLoading,
    composeLoading,
    clearLoading,
    error,
    hasError,
    clearError,
    fetchList,
    loadMore,
    refreshHead,
    fetchDetail,
    clearInbox,
    composeMessage,
    reset
  }
})
